/**
 * Channel map — maps ElectronAPI method names to IPC channels.
 *
 * Derived from preload/index.ts. This is the single source of truth for
 * the method→channel mapping used by buildClientApi().
 */

import { RPC_CHANNELS } from '../shared/types'
import { GIT_BASH_INSTALL_TIMEOUT_MS } from '../shared/git-bash'
import {
  OKCLAW_INVOICE_CLIENT_TIMEOUT_MS,
  OKCLAW_INVOICE_DOWNLOAD_CLIENT_TIMEOUT_MS,
} from '@okclawpro/shared/okclaw-invoice/timeout-budget'
import type { ChannelMap } from './build-api'

const SKILL_MARKET_INSTALL_TIMEOUT_MS = 5 * 60 * 1000
const SOURCE_MARKET_INSTALL_TIMEOUT_MS = 5 * 60 * 1000

function invoke(
  channel: string,
  options: {
    transform?: (result: any) => any
    timeoutMs?: number
    chunked?: { largeArgIndex: number; thresholdBytes?: number }
  } = {},
) {
  return { type: 'invoke' as const, channel, ...options }
}

function listener(channel: string) {
  return { type: 'listener' as const, channel }
}

export const CHANNEL_MAP = {
  // Session management
  getSessions: invoke(RPC_CHANNELS.sessions.GET),
  getUnreadSummary: invoke(RPC_CHANNELS.sessions.GET_UNREAD_SUMMARY),
  markAllSessionsRead: invoke(RPC_CHANNELS.sessions.MARK_ALL_READ),
  getSessionMessages: invoke(RPC_CHANNELS.sessions.GET_MESSAGES),
  createSession: invoke(RPC_CHANNELS.sessions.CREATE),
  deleteSession: invoke(RPC_CHANNELS.sessions.DELETE),
  sendMessage: invoke(RPC_CHANNELS.sessions.SEND_MESSAGE, {
    chunked: { largeArgIndex: 2 },
    timeoutMs: 120_000,
  }),
  cancelProcessing: invoke(RPC_CHANNELS.sessions.CANCEL),
  killShell: invoke(RPC_CHANNELS.sessions.KILL_SHELL),
  getTaskOutput: invoke(RPC_CHANNELS.tasks.GET_OUTPUT),
  validateTask: invoke(RPC_CHANNELS.tasks.VALIDATE),
  createTask: invoke(RPC_CHANNELS.tasks.CREATE),
  generateTask: invoke(RPC_CHANNELS.tasks.GENERATE),
  runTask: invoke(RPC_CHANNELS.tasks.RUN),
  pauseTask: invoke(RPC_CHANNELS.tasks.PAUSE),
  resumeTask: invoke(RPC_CHANNELS.tasks.RESUME),
  stopTask: invoke(RPC_CHANNELS.tasks.STOP),
  getTask: invoke(RPC_CHANNELS.tasks.GET),
  listTasks: invoke(RPC_CHANNELS.tasks.LIST),
  getTaskResults: invoke(RPC_CHANNELS.tasks.GET_RESULTS),
  onTaskGenerated: listener(RPC_CHANNELS.tasks.GENERATED),
  respondToPermission: invoke(RPC_CHANNELS.sessions.RESPOND_TO_PERMISSION),
  respondToCredential: invoke(RPC_CHANNELS.sessions.RESPOND_TO_CREDENTIAL),
  sessionCommand: invoke(RPC_CHANNELS.sessions.COMMAND),
  exportSession: invoke(RPC_CHANNELS.sessions.EXPORT),
  importSession: invoke(RPC_CHANNELS.sessions.IMPORT),
  exportRemoteSessionTransfer: invoke(RPC_CHANNELS.sessions.EXPORT_REMOTE_TRANSFER),
  importRemoteSessionTransfer: invoke(RPC_CHANNELS.sessions.IMPORT_REMOTE_TRANSFER),
  getPendingPlanExecution: invoke(RPC_CHANNELS.sessions.GET_PENDING_PLAN_EXECUTION),
  getSessionPermissionModeState: invoke(RPC_CHANNELS.sessions.GET_PERMISSION_MODE_STATE),

  // Event listeners
  onSessionEvent: listener(RPC_CHANNELS.sessions.EVENT),
  onUnreadSummaryChanged: listener(RPC_CHANNELS.sessions.UNREAD_SUMMARY_CHANGED),

  // Transport reliability
  onReconnected: listener('__transport:reconnected'),

  // Workspace management
  getWorkspaces: invoke(RPC_CHANNELS.workspaces.GET),
  createWorkspace: invoke(RPC_CHANNELS.workspaces.CREATE),
  checkWorkspaceSlug: invoke(RPC_CHANNELS.workspaces.CHECK_SLUG),
  updateWorkspaceRemoteServer: invoke(RPC_CHANNELS.workspaces.UPDATE_REMOTE),
  testRemoteConnection: invoke(RPC_CHANNELS.remote.TEST_CONNECTION),

  // Server-level workspace operations (REMOTE_ELIGIBLE)
  getServerWorkspaces: invoke(RPC_CHANNELS.server.GET_WORKSPACES),
  createServerWorkspace: invoke(RPC_CHANNELS.server.CREATE_WORKSPACE),

  // Window management
  getWindowWorkspace: invoke(RPC_CHANNELS.window.GET_WORKSPACE),
  getWindowMode: invoke(RPC_CHANNELS.window.GET_MODE),
  openWorkspace: invoke(RPC_CHANNELS.window.OPEN_WORKSPACE),
  openSessionInNewWindow: invoke(RPC_CHANNELS.window.OPEN_SESSION_IN_NEW_WINDOW),
  switchWorkspace: invoke(RPC_CHANNELS.window.SWITCH_WORKSPACE),
  closeWindow: invoke(RPC_CHANNELS.window.CLOSE),
  confirmCloseWindow: invoke(RPC_CHANNELS.window.CONFIRM_CLOSE),
  cancelCloseWindow: invoke(RPC_CHANNELS.window.CANCEL_CLOSE),
  onCloseRequested: listener(RPC_CHANNELS.window.CLOSE_REQUESTED),
  setTrafficLightsVisible: invoke(RPC_CHANNELS.window.SET_TRAFFIC_LIGHTS),

  // File operations
  readFile: invoke(RPC_CHANNELS.file.READ),
  getFileSize: invoke(RPC_CHANNELS.file.STAT),
  readFileDataUrl: invoke(RPC_CHANNELS.file.READ_DATA_URL),
  readFilePreviewDataUrl: invoke(RPC_CHANNELS.file.READ_PREVIEW_DATA_URL),
  readFileBinary: invoke(RPC_CHANNELS.file.READ_BINARY),
  openFileDialog: invoke(RPC_CHANNELS.file.OPEN_DIALOG),
  readFileAttachment: invoke(RPC_CHANNELS.file.READ_ATTACHMENT),
  readUserAttachment: invoke(RPC_CHANNELS.file.READ_USER_ATTACHMENT),
  reauthorizeSessionFileReference: invoke(RPC_CHANNELS.file.REAUTHORIZE_REFERENCE),
  storeAttachment: invoke(RPC_CHANNELS.file.STORE_ATTACHMENT, {
    chunked: { largeArgIndex: 1 },
    timeoutMs: 5 * 60_000,
  }),
  generateThumbnail: invoke(RPC_CHANNELS.file.GENERATE_THUMBNAIL),
  listServerFiles: invoke(RPC_CHANNELS.file.LIST_SERVER_FILES),
  prepareFileDownload: invoke(RPC_CHANNELS.file.PREPARE_DOWNLOAD),
  readFileDownloadChunk: invoke(RPC_CHANNELS.file.READ_DOWNLOAD_CHUNK),
  finishFileDownload: invoke(RPC_CHANNELS.file.FINISH_DOWNLOAD),

  // Theme
  getSystemTheme: invoke(RPC_CHANNELS.theme.GET_SYSTEM_PREFERENCE),
  onSystemThemeChange: listener(RPC_CHANNELS.theme.SYSTEM_CHANGED),

  // System
  getVersions: invoke(RPC_CHANNELS.system.VERSIONS),
  getHomeDir: invoke(RPC_CHANNELS.system.HOME_DIR),
  isDebugMode: invoke(RPC_CHANNELS.system.IS_DEBUG_MODE),
  getWindowBackgroundMaterial: invoke(RPC_CHANNELS.system.GET_WINDOW_BACKGROUND_MATERIAL),

  // Auto-update
  checkForUpdates: invoke(RPC_CHANNELS.update.CHECK),
  downloadUpdate: invoke(RPC_CHANNELS.update.DOWNLOAD),
  getUpdateInfo: invoke(RPC_CHANNELS.update.GET_INFO),
  installUpdate: invoke(RPC_CHANNELS.update.INSTALL),
  dismissUpdate: invoke(RPC_CHANNELS.update.DISMISS),
  getDismissedUpdateVersion: invoke(RPC_CHANNELS.update.GET_DISMISSED),
  onUpdateAvailable: listener(RPC_CHANNELS.update.AVAILABLE),
  onUpdateDownloadProgress: listener(RPC_CHANNELS.update.DOWNLOAD_PROGRESS),

  // Release notes
  getReleaseNotes: invoke(RPC_CHANNELS.releaseNotes.GET),
  getLatestReleaseVersion: invoke(RPC_CHANNELS.releaseNotes.GET_LATEST_VERSION),

  // Shell operations
  openUrl: invoke(RPC_CHANNELS.shell.OPEN_URL),
  openFile: invoke(RPC_CHANNELS.shell.OPEN_FILE),
  showInFolder: invoke(RPC_CHANNELS.shell.SHOW_IN_FOLDER),

  // Menu event listeners
  onMenuNewChat: listener(RPC_CHANNELS.menu.NEW_CHAT),
  onMenuOpenSettings: listener(RPC_CHANNELS.menu.OPEN_SETTINGS),
  onMenuKeyboardShortcuts: listener(RPC_CHANNELS.menu.KEYBOARD_SHORTCUTS),
  onMenuToggleFocusMode: listener(RPC_CHANNELS.menu.TOGGLE_FOCUS_MODE),
  onMenuToggleSidebar: listener(RPC_CHANNELS.menu.TOGGLE_SIDEBAR),

  // Deep link
  onDeepLinkNavigate: listener(RPC_CHANNELS.deeplink.NAVIGATE),

  // Auth
  showLogoutConfirmation: invoke(RPC_CHANNELS.auth.SHOW_LOGOUT_CONFIRMATION),
  showDeleteSessionConfirmation: invoke(RPC_CHANNELS.auth.SHOW_DELETE_SESSION_CONFIRMATION),
  logout: invoke(RPC_CHANNELS.auth.LOGOUT),
  getCredentialHealth: invoke(RPC_CHANNELS.credentials.HEALTH_CHECK),

  // Onboarding
  getOnboardingState: invoke(RPC_CHANNELS.onboarding.GET_STATE),
  saveOnboardingProgress: invoke(RPC_CHANNELS.onboarding.SAVE_PROGRESS),
  checkOnboardingReadiness: invoke(RPC_CHANNELS.onboarding.CHECK_READINESS),
  retryOnboardingReadinessItem: invoke(RPC_CHANNELS.onboarding.RETRY_READINESS_ITEM),
  deferOnboarding: invoke(RPC_CHANNELS.onboarding.DEFER),
  completeOnboarding: invoke(RPC_CHANNELS.onboarding.COMPLETE),
  recordOnboardingLocalEvent: invoke(RPC_CHANNELS.onboarding.RECORD_LOCAL_EVENT),
  resetOnboarding: invoke(RPC_CHANNELS.onboarding.RESET),
  getAuthState: invoke(RPC_CHANNELS.onboarding.GET_AUTH_STATE),
  getSetupNeeds: invoke(RPC_CHANNELS.onboarding.GET_AUTH_STATE, { transform: r => r.setupNeeds }),
  startWorkspaceMcpOAuth: invoke(RPC_CHANNELS.onboarding.START_MCP_OAUTH),
  startClaudeOAuth: invoke(RPC_CHANNELS.onboarding.START_CLAUDE_OAUTH),
  exchangeClaudeCode: invoke(RPC_CHANNELS.onboarding.EXCHANGE_CLAUDE_CODE),
  hasClaudeOAuthState: invoke(RPC_CHANNELS.onboarding.HAS_CLAUDE_OAUTH_STATE),
  clearClaudeOAuthState: invoke(RPC_CHANNELS.onboarding.CLEAR_CLAUDE_OAUTH_STATE),
  deferSetup: invoke(RPC_CHANNELS.onboarding.DEFER_SETUP),

  // ChatGPT OAuth
  startChatGptOAuth: invoke(RPC_CHANNELS.chatgpt.START_OAUTH),
  cancelChatGptOAuth: invoke(RPC_CHANNELS.chatgpt.CANCEL_OAUTH),
  getChatGptAuthStatus: invoke(RPC_CHANNELS.chatgpt.GET_AUTH_STATUS),
  chatGptLogout: invoke(RPC_CHANNELS.chatgpt.LOGOUT),

  // GitHub Copilot OAuth
  startCopilotOAuth: invoke(RPC_CHANNELS.copilot.START_OAUTH),
  cancelCopilotOAuth: invoke(RPC_CHANNELS.copilot.CANCEL_OAUTH),
  getCopilotAuthStatus: invoke(RPC_CHANNELS.copilot.GET_AUTH_STATUS),
  copilotLogout: invoke(RPC_CHANNELS.copilot.LOGOUT),
  onCopilotDeviceCode: listener(RPC_CHANNELS.copilot.DEVICE_CODE),

  // xAI OAuth
  startXaiOAuth: invoke(RPC_CHANNELS.xai.START_OAUTH),
  cancelXaiOAuth: invoke(RPC_CHANNELS.xai.CANCEL_OAUTH),
  getXaiAuthStatus: invoke(RPC_CHANNELS.xai.GET_AUTH_STATUS),
  xaiLogout: invoke(RPC_CHANNELS.xai.LOGOUT),
  onXaiDeviceCode: listener(RPC_CHANNELS.xai.DEVICE_CODE),

  setupLlmConnection: invoke(RPC_CHANNELS.settings.SETUP_LLM_CONNECTION),
  testLlmConnectionSetup: invoke(RPC_CHANNELS.settings.TEST_LLM_CONNECTION_SETUP),

  // Server info (REMOTE_ELIGIBLE)
  getServerHomeDir: invoke(RPC_CHANNELS.server.HOME_DIR),

  // Server mode configuration
  getServerConfig: invoke(RPC_CHANNELS.settings.GET_SERVER_CONFIG),
  setServerConfig: invoke(RPC_CHANNELS.settings.SET_SERVER_CONFIG),
  getServerStatus: invoke(RPC_CHANNELS.settings.GET_SERVER_STATUS),
  resetServerAccessToken: invoke(RPC_CHANNELS.settings.RESET_SERVER_ACCESS_TOKEN),
  disconnectPublicAccessConnection: invoke(RPC_CHANNELS.settings.DISCONNECT_PUBLIC_ACCESS_CONNECTION),
  deletePublicAccessDevice: invoke(RPC_CHANNELS.settings.DELETE_PUBLIC_ACCESS_DEVICE),
  getWebDavSyncSettings: invoke(RPC_CHANNELS.settings.GET_WEBDAV_SYNC_SETTINGS),
  saveWebDavSyncSettings: invoke(RPC_CHANNELS.settings.SAVE_WEBDAV_SYNC_SETTINGS),
  testWebDavSyncConnection: invoke(RPC_CHANNELS.settings.TEST_WEBDAV_SYNC_CONNECTION),
  runWebDavBackup: invoke(RPC_CHANNELS.settings.RUN_WEBDAV_BACKUP),
  getWebDavLatestSnapshot: invoke(RPC_CHANNELS.settings.GET_WEBDAV_LATEST_SNAPSHOT),
  runWebDavRestore: invoke(RPC_CHANNELS.settings.RUN_WEBDAV_RESTORE),
  onWebDavSyncStatusChanged: listener(RPC_CHANNELS.settings.WEBDAV_SYNC_STATUS_CHANGED),

  getDefaultThinkingLevel: invoke(RPC_CHANNELS.settings.GET_DEFAULT_THINKING_LEVEL),
  setDefaultThinkingLevel: invoke(RPC_CHANNELS.settings.SET_DEFAULT_THINKING_LEVEL),
  getNetworkProxySettings: invoke(RPC_CHANNELS.settings.GET_NETWORK_PROXY),
  setNetworkProxySettings: invoke(RPC_CHANNELS.settings.SET_NETWORK_PROXY),

  // Desktop pet
  getPetSettings: invoke(RPC_CHANNELS.pet.GET_SETTINGS),
  setPetSettings: invoke(RPC_CHANNELS.pet.SET_SETTINGS),
  listPets: invoke(RPC_CHANNELS.pet.LIST),
  showPet: invoke(RPC_CHANNELS.pet.SHOW),
  hidePet: invoke(RPC_CHANNELS.pet.HIDE),
  refreshPets: invoke(RPC_CHANNELS.pet.REFRESH),
  openCustomPetsDir: invoke(RPC_CHANNELS.pet.OPEN_CUSTOM_PETS_DIR),
  onPetStateChanged: listener(RPC_CHANNELS.pet.STATE_CHANGED),

  // Session-specific model
  getSessionModel: invoke(RPC_CHANNELS.sessions.GET_MODEL),
  setSessionModel: invoke(RPC_CHANNELS.sessions.SET_MODEL),
  setSessionModelRoute: invoke(RPC_CHANNELS.sessions.SET_MODEL_ROUTE),

  // Workspace Settings
  getWorkspaceSettings: invoke(RPC_CHANNELS.workspace.SETTINGS_GET),
  updateWorkspaceSetting: invoke(RPC_CHANNELS.workspace.SETTINGS_UPDATE),

  // Folder dialog
  openFolderDialog: invoke(RPC_CHANNELS.dialog.OPEN_FOLDER),

  // Filesystem search
  searchFiles: invoke(RPC_CHANNELS.fs.SEARCH),

  // Server filesystem browsing (remote mode)
  listServerDirectory: invoke(RPC_CHANNELS.fs.LIST_DIRECTORY),

  // Debug logging
  debugLog: invoke(RPC_CHANNELS.debug.LOG),

  // User Preferences
  readPreferences: invoke(RPC_CHANNELS.preferences.READ),
  writePreferences: invoke(RPC_CHANNELS.preferences.WRITE),

  // Session Drafts
  getDraft: invoke(RPC_CHANNELS.drafts.GET),
  setDraft: invoke(RPC_CHANNELS.drafts.SET),
  deleteDraft: invoke(RPC_CHANNELS.drafts.DELETE),
  getAllDrafts: invoke(RPC_CHANNELS.drafts.GET_ALL),

  // Session Info Panel
  getSessionFiles: invoke(RPC_CHANNELS.sessions.GET_FILES),
  getSessionFolderPath: invoke(RPC_CHANNELS.sessions.GET_FOLDER_PATH),
  getSessionNotes: invoke(RPC_CHANNELS.sessions.GET_NOTES),
  setSessionNotes: invoke(RPC_CHANNELS.sessions.SET_NOTES),
  watchSessionFiles: invoke(RPC_CHANNELS.sessions.WATCH_FILES),
  unwatchSessionFiles: invoke(RPC_CHANNELS.sessions.UNWATCH_FILES),
  onSessionFilesChanged: listener(RPC_CHANNELS.sessions.FILES_CHANGED),

  // Sources
  getSources: invoke(RPC_CHANNELS.sources.GET),
  createSource: invoke(RPC_CHANNELS.sources.CREATE),
  deleteSource: invoke(RPC_CHANNELS.sources.DELETE),
  startSourceOAuth: invoke(RPC_CHANNELS.sources.START_OAUTH),
  saveSourceCredentials: invoke(RPC_CHANNELS.sources.SAVE_CREDENTIALS),
  getSourcePermissionsConfig: invoke(RPC_CHANNELS.sources.GET_PERMISSIONS),
  getWorkspacePermissionsConfig: invoke(RPC_CHANNELS.workspace.GET_PERMISSIONS),
  getDefaultPermissionsConfig: invoke(RPC_CHANNELS.permissions.GET_DEFAULTS),
  onDefaultPermissionsChanged: listener(RPC_CHANNELS.permissions.DEFAULTS_CHANGED),
  getMcpTools: invoke(RPC_CHANNELS.sources.GET_MCP_TOOLS),

  // Source marketplace (git 模板市场)
  getSourceMarketCategories: invoke(RPC_CHANNELS.sources.MARKET_CATEGORIES),
  searchSourceMarket: invoke(RPC_CHANNELS.sources.MARKET_SEARCH),
  listSourceMarket: invoke(RPC_CHANNELS.sources.MARKET_LIST),
  installSourceFromMarket: invoke(RPC_CHANNELS.sources.MARKET_INSTALL, { timeoutMs: SOURCE_MARKET_INSTALL_TIMEOUT_MS }),
  createSourceFromMarket: invoke(RPC_CHANNELS.sources.MARKET_CREATE, { timeoutMs: SOURCE_MARKET_INSTALL_TIMEOUT_MS }),

  // Session content search
  searchSessionContent: invoke(RPC_CHANNELS.sessions.SEARCH_CONTENT),

  // OAuth (server-owned credentials)
  oauthRevoke: invoke(RPC_CHANNELS.oauth.REVOKE),

  // Sources change listener
  onSourcesChanged: listener(RPC_CHANNELS.sources.CHANGED),

  // Skills
  getSkills: invoke(RPC_CHANNELS.skills.GET),
  getSkillFiles: invoke(RPC_CHANNELS.skills.GET_FILES),
  getSkillMarketCategories: invoke(RPC_CHANNELS.skills.MARKET_CATEGORIES),
  searchSkillMarket: invoke(RPC_CHANNELS.skills.MARKET_SEARCH),
  listSkillMarket: invoke(RPC_CHANNELS.skills.MARKET_LIST),
  installSkillFromMarket: invoke(RPC_CHANNELS.skills.MARKET_INSTALL, { timeoutMs: SKILL_MARKET_INSTALL_TIMEOUT_MS }),
  teamSkillApi: invoke(RPC_CHANNELS.skills.TEAM_API, { timeoutMs: SKILL_MARKET_INSTALL_TIMEOUT_MS }),
  packageTeamSkill: invoke(RPC_CHANNELS.skills.TEAM_PACKAGE_SKILL, { timeoutMs: SKILL_MARKET_INSTALL_TIMEOUT_MS }),
  installTeamSkillPackage: invoke(RPC_CHANNELS.skills.TEAM_INSTALL_PACKAGE, { timeoutMs: SKILL_MARKET_INSTALL_TIMEOUT_MS }),
  deleteSkill: invoke(RPC_CHANNELS.skills.DELETE),
  importSkill: invoke(RPC_CHANNELS.skills.IMPORT),
  importSkillFromPath: invoke(RPC_CHANNELS.skills.IMPORT_FROM_PATH),
  cloneSkillToWorkspace: invoke(RPC_CHANNELS.skills.CLONE_TO_WORKSPACE),
  openSkillInEditor: invoke(RPC_CHANNELS.skills.OPEN_EDITOR),
  openSkillInFinder: invoke(RPC_CHANNELS.skills.OPEN_FINDER),
  onSkillsChanged: listener(RPC_CHANNELS.skills.CHANGED),

  // Statuses
  listStatuses: invoke(RPC_CHANNELS.statuses.LIST),
  reorderStatuses: invoke(RPC_CHANNELS.statuses.REORDER),
  onStatusesChanged: listener(RPC_CHANNELS.statuses.CHANGED),

  // Labels
  listLabels: invoke(RPC_CHANNELS.labels.LIST),
  createLabel: invoke(RPC_CHANNELS.labels.CREATE),
  deleteLabel: invoke(RPC_CHANNELS.labels.DELETE),
  onLabelsChanged: listener(RPC_CHANNELS.labels.CHANGED),

  // LLM connections change listener
  onLlmConnectionsChanged: listener(RPC_CHANNELS.llmConnections.CHANGED),

  // Views
  listViews: invoke(RPC_CHANNELS.views.LIST),
  saveViews: invoke(RPC_CHANNELS.views.SAVE),

  // Tool icon mappings
  getToolIconMappings: invoke(RPC_CHANNELS.toolIcons.GET_MAPPINGS),

  // Workspace images
  readWorkspaceImage: invoke(RPC_CHANNELS.workspace.READ_IMAGE),
  writeWorkspaceImage: invoke(RPC_CHANNELS.workspace.WRITE_IMAGE),

  // Theme
  getAppTheme: invoke(RPC_CHANNELS.theme.GET_APP),
  loadPresetThemes: invoke(RPC_CHANNELS.theme.GET_PRESETS),
  loadPresetTheme: invoke(RPC_CHANNELS.theme.LOAD_PRESET),
  getColorTheme: invoke(RPC_CHANNELS.theme.GET_COLOR_THEME),
  setColorTheme: invoke(RPC_CHANNELS.theme.SET_COLOR_THEME),
  getWorkspaceColorTheme: invoke(RPC_CHANNELS.theme.GET_WORKSPACE_COLOR_THEME),
  setWorkspaceColorTheme: invoke(RPC_CHANNELS.theme.SET_WORKSPACE_COLOR_THEME),
  getAllWorkspaceThemes: invoke(RPC_CHANNELS.theme.GET_ALL_WORKSPACE_THEMES),
  getLogoUrl: invoke(RPC_CHANNELS.logo.GET_URL),
  onAppThemeChange: listener(RPC_CHANNELS.theme.APP_CHANGED),
  broadcastThemePreferences: invoke(RPC_CHANNELS.theme.BROADCAST_PREFERENCES),
  onThemePreferencesChange: listener(RPC_CHANNELS.theme.PREFERENCES_CHANGED),
  broadcastWorkspaceThemeChange: invoke(RPC_CHANNELS.theme.BROADCAST_WORKSPACE_THEME),
  onWorkspaceThemeChange: listener(RPC_CHANNELS.theme.WORKSPACE_THEME_CHANGED),

  // Notifications
  showNotification: invoke(RPC_CHANNELS.notification.SHOW),
  getNotificationsEnabled: invoke(RPC_CHANNELS.notification.GET_ENABLED),
  setNotificationsEnabled: invoke(RPC_CHANNELS.notification.SET_ENABLED),

  // Input settings
  getAutoCapitalisation: invoke(RPC_CHANNELS.input.GET_AUTO_CAPITALISATION),
  setAutoCapitalisation: invoke(RPC_CHANNELS.input.SET_AUTO_CAPITALISATION),
  getSendMessageKey: invoke(RPC_CHANNELS.input.GET_SEND_MESSAGE_KEY),
  setSendMessageKey: invoke(RPC_CHANNELS.input.SET_SEND_MESSAGE_KEY),
  getSpellCheck: invoke(RPC_CHANNELS.input.GET_SPELL_CHECK),
  setSpellCheck: invoke(RPC_CHANNELS.input.SET_SPELL_CHECK),

  // Power settings
  getKeepAwakeWhileRunning: invoke(RPC_CHANNELS.power.GET_KEEP_AWAKE),
  setKeepAwakeWhileRunning: invoke(RPC_CHANNELS.power.SET_KEEP_AWAKE),

  // Appearance settings
  getRichToolDescriptions: invoke(RPC_CHANNELS.appearance.GET_RICH_TOOL_DESCRIPTIONS),
  setRichToolDescriptions: invoke(RPC_CHANNELS.appearance.SET_RICH_TOOL_DESCRIPTIONS),

  // Tools settings
  getBrowserToolEnabled: invoke(RPC_CHANNELS.tools.GET_BROWSER_TOOL_ENABLED),
  setBrowserToolEnabled: invoke(RPC_CHANNELS.tools.SET_BROWSER_TOOL_ENABLED),
  getNanoBananaEnabled: invoke(RPC_CHANNELS.tools.GET_NANO_BANANA_ENABLED),
  setNanoBananaEnabled: invoke(RPC_CHANNELS.tools.SET_NANO_BANANA_ENABLED),
  getImageGenerationProvider: invoke(RPC_CHANNELS.tools.GET_IMAGE_GENERATION_PROVIDER),
  setImageGenerationProvider: invoke(RPC_CHANNELS.tools.SET_IMAGE_GENERATION_PROVIDER),
  getImageUnderstandingEnabled: invoke(RPC_CHANNELS.tools.GET_IMAGE_UNDERSTANDING_ENABLED),
  setImageUnderstandingEnabled: invoke(RPC_CHANNELS.tools.SET_IMAGE_UNDERSTANDING_ENABLED),
  getBuiltInImageToolRoutes: invoke(RPC_CHANNELS.tools.GET_BUILT_IN_IMAGE_TOOL_ROUTES),
  setBuiltInImageToolRoutes: invoke(RPC_CHANNELS.tools.SET_BUILT_IN_IMAGE_TOOL_ROUTES),

  // Prompt caching & context
  getExtendedPromptCache: invoke(RPC_CHANNELS.caching.GET_EXTENDED_PROMPT_CACHE),
  setExtendedPromptCache: invoke(RPC_CHANNELS.caching.SET_EXTENDED_PROMPT_CACHE),
  getEnable1MContext: invoke(RPC_CHANNELS.caching.GET_ENABLE_1M_CONTEXT),
  setEnable1MContext: invoke(RPC_CHANNELS.caching.SET_ENABLE_1M_CONTEXT),

  // Badge
  refreshBadge: invoke(RPC_CHANNELS.badge.REFRESH),
  setDockIconWithBadge: invoke(RPC_CHANNELS.badge.SET_ICON),
  onBadgeDraw: listener(RPC_CHANNELS.badge.DRAW),
  onBadgeDrawWindows: listener(RPC_CHANNELS.badge.DRAW_WINDOWS),

  // Window focus
  getWindowFocusState: invoke(RPC_CHANNELS.window.GET_FOCUS_STATE),
  onWindowFocusChange: listener(RPC_CHANNELS.window.FOCUS_STATE),
  onNotificationNavigate: listener(RPC_CHANNELS.notification.NAVIGATE),

  // Git
  getGitBranch: invoke(RPC_CHANNELS.git.GET_BRANCH),
  checkGitBash: invoke(RPC_CHANNELS.gitbash.CHECK),
  browseForGitBash: invoke(RPC_CHANNELS.gitbash.BROWSE),
  setGitBashPath: invoke(RPC_CHANNELS.gitbash.SET_PATH),
  installGitBash: invoke(RPC_CHANNELS.gitbash.INSTALL, { timeoutMs: GIT_BASH_INSTALL_TIMEOUT_MS }),

  // Menu actions
  menuQuit: invoke(RPC_CHANNELS.menu.QUIT),
  menuNewWindow: invoke(RPC_CHANNELS.menu.NEW_WINDOW),
  menuMinimize: invoke(RPC_CHANNELS.menu.MINIMIZE),
  menuMaximize: invoke(RPC_CHANNELS.menu.MAXIMIZE),
  menuZoomIn: invoke(RPC_CHANNELS.menu.ZOOM_IN),
  menuZoomOut: invoke(RPC_CHANNELS.menu.ZOOM_OUT),
  menuZoomReset: invoke(RPC_CHANNELS.menu.ZOOM_RESET),
  menuToggleDevTools: invoke(RPC_CHANNELS.menu.TOGGLE_DEV_TOOLS),
  menuUndo: invoke(RPC_CHANNELS.menu.UNDO),
  menuRedo: invoke(RPC_CHANNELS.menu.REDO),
  menuCut: invoke(RPC_CHANNELS.menu.CUT),
  menuCopy: invoke(RPC_CHANNELS.menu.COPY),
  menuPaste: invoke(RPC_CHANNELS.menu.PASTE),
  menuSelectAll: invoke(RPC_CHANNELS.menu.SELECT_ALL),

  // Browser pane management
  'browserPane.create': invoke(RPC_CHANNELS.browserPane.CREATE),
  'browserPane.destroy': invoke(RPC_CHANNELS.browserPane.DESTROY),
  'browserPane.list': invoke(RPC_CHANNELS.browserPane.LIST),
  'browserPane.navigate': invoke(RPC_CHANNELS.browserPane.NAVIGATE),
  'browserPane.goBack': invoke(RPC_CHANNELS.browserPane.GO_BACK),
  'browserPane.goForward': invoke(RPC_CHANNELS.browserPane.GO_FORWARD),
  'browserPane.reload': invoke(RPC_CHANNELS.browserPane.RELOAD),
  'browserPane.stop': invoke(RPC_CHANNELS.browserPane.STOP),
  'browserPane.focus': invoke(RPC_CHANNELS.browserPane.FOCUS),
  'browserPane.createTab': invoke(RPC_CHANNELS.browserPane.CREATE_TAB),
  'browserPane.activateTab': invoke(RPC_CHANNELS.browserPane.ACTIVATE_TAB),
  'browserPane.setAgentTab': invoke(RPC_CHANNELS.browserPane.SET_AGENT_TAB),
  'browserPane.closeTab': invoke(RPC_CHANNELS.browserPane.CLOSE_TAB),
  'browserPane.onStateChanged': listener(RPC_CHANNELS.browserPane.STATE_CHANGED),
  'browserPane.onRemoved': listener(RPC_CHANNELS.browserPane.REMOVED),
  'browserPane.onInteracted': listener(RPC_CHANNELS.browserPane.INTERACTED),

  // LLM Connections
  listLlmConnections: invoke(RPC_CHANNELS.llmConnections.LIST),
  listLlmConnectionsWithStatus: invoke(RPC_CHANNELS.llmConnections.LIST_WITH_STATUS),
  getLlmConnection: invoke(RPC_CHANNELS.llmConnections.GET),
  getLlmConnectionApiKey: invoke(RPC_CHANNELS.llmConnections.GET_API_KEY),
  saveLlmConnection: invoke(RPC_CHANNELS.llmConnections.SAVE),
  deleteLlmConnection: invoke(RPC_CHANNELS.llmConnections.DELETE),
  testLlmConnection: invoke(RPC_CHANNELS.llmConnections.TEST),
  setDefaultLlmConnection: invoke(RPC_CHANNELS.llmConnections.SET_DEFAULT),
  setWorkspaceDefaultLlmConnection: invoke(RPC_CHANNELS.llmConnections.SET_WORKSPACE_DEFAULT),
  refreshLlmModels: invoke(RPC_CHANNELS.llmConnections.REFRESH_MODELS),

  // Projects
  getProjects: invoke(RPC_CHANNELS.projects.GET),
  getProject: invoke(RPC_CHANNELS.projects.GET_ONE),
  createProject: invoke(RPC_CHANNELS.projects.CREATE),
  updateProject: invoke(RPC_CHANNELS.projects.UPDATE),
  deleteProject: invoke(RPC_CHANNELS.projects.DELETE),
  listProjectAssets: invoke(RPC_CHANNELS.projects.LIST_ASSETS),
  uploadProjectAsset: invoke(RPC_CHANNELS.projects.UPLOAD_ASSET, {
    chunked: { largeArgIndex: 2 },
    timeoutMs: 5 * 60_000,
  }),
  deleteProjectAsset: invoke(RPC_CHANNELS.projects.DELETE_ASSET),
  onProjectsChanged: listener(RPC_CHANNELS.projects.CHANGED),

  // Context memory and docsets
  getContextState: invoke(RPC_CHANNELS.context.GET_STATE),
  saveContextConfig: invoke(RPC_CHANNELS.context.SAVE_CONFIG),
  saveContextMemory: invoke(RPC_CHANNELS.context.SAVE_MEMORY),
  clearContextMemory: invoke(RPC_CHANNELS.context.CLEAR_MEMORY),
  refineContextMemory: invoke(RPC_CHANNELS.context.REFINE_MEMORY),
  createContextDocset: invoke(RPC_CHANNELS.context.CREATE_DOCSET),
  updateContextDocset: invoke(RPC_CHANNELS.context.UPDATE_DOCSET),
  deleteContextDocset: invoke(RPC_CHANNELS.context.DELETE_DOCSET),
  addContextDocsetFiles: invoke(RPC_CHANNELS.context.ADD_FILES),
  addContextDocsetUrl: invoke(RPC_CHANNELS.context.ADD_URL),
  rebuildContextDocsetIndex: invoke(RPC_CHANNELS.context.REBUILD_INDEX),
  searchContextDocsets: invoke(RPC_CHANNELS.context.SEARCH_DOCSETS),

  // Automations
  getAutomationsConfig: invoke(RPC_CHANNELS.automations.GET_CONFIG),
  upsertAutomation: invoke(RPC_CHANNELS.automations.UPSERT),
  testAutomation: invoke(RPC_CHANNELS.automations.TEST),
  setAutomationEnabled: invoke(RPC_CHANNELS.automations.SET_ENABLED),
  duplicateAutomation: invoke(RPC_CHANNELS.automations.DUPLICATE),
  deleteAutomation: invoke(RPC_CHANNELS.automations.DELETE),
  getAutomationHistory: invoke(RPC_CHANNELS.automations.GET_HISTORY),
  getAutomationLastExecuted: invoke(RPC_CHANNELS.automations.GET_LAST_EXECUTED),
  replayAutomation: invoke(RPC_CHANNELS.automations.REPLAY),
  onAutomationsChanged: listener(RPC_CHANNELS.automations.CHANGED),

  // OKClaw Auth
  okclawAuthGetState: invoke(RPC_CHANNELS.okclawAuth.GET_STATE),
  okclawAuthLoginPassword: invoke(RPC_CHANNELS.okclawAuth.LOGIN_PASSWORD),
  okclawAuthLoginSms: invoke(RPC_CHANNELS.okclawAuth.LOGIN_SMS),
  okclawAuthLoginWechat: invoke(RPC_CHANNELS.okclawAuth.LOGIN_WECHAT),
  okclawAuthSendSms: invoke(RPC_CHANNELS.okclawAuth.SEND_SMS),
  okclawAuthGetCaptcha: invoke(RPC_CHANNELS.okclawAuth.GET_CAPTCHA),
  okclawAuthSendRegistrySms: invoke(RPC_CHANNELS.okclawAuth.SEND_REGISTRY_SMS),
  okclawAuthRegister: invoke(RPC_CHANNELS.okclawAuth.REGISTER),
  okclawAuthResetPassword: invoke(RPC_CHANNELS.okclawAuth.RESET_PASSWORD),
  okclawAuthWechatQr: invoke(RPC_CHANNELS.okclawAuth.WECHAT_QR),
  okclawAuthWechatStatus: invoke(RPC_CHANNELS.okclawAuth.WECHAT_STATUS),
  okclawAuthWechatBindQr: invoke(RPC_CHANNELS.okclawAuth.WECHAT_BIND_QR),
  okclawAuthWechatBindStatus: invoke(RPC_CHANNELS.okclawAuth.WECHAT_BIND_STATUS),
  okclawAuthGetCurrentUserInfo: invoke(RPC_CHANNELS.okclawAuth.CURRENT_USER_INFO),
  okclawAuthGetCurrentUserBalance: invoke(RPC_CHANNELS.okclawAuth.CURRENT_USER_BALANCE),
  okclawAuthGetStudentCertificationInfo: invoke(RPC_CHANNELS.okclawAuth.STUDENT_CERTIFICATION_INFO),
  okclawAuthUpdateUserName: invoke(RPC_CHANNELS.okclawAuth.UPDATE_USER_NAME),
  okclawAuthSendUserPhoneValidate: invoke(RPC_CHANNELS.okclawAuth.SEND_USER_PHONE_VALIDATE),
  okclawAuthSendNewPhoneValidate: invoke(RPC_CHANNELS.okclawAuth.SEND_NEW_PHONE_VALIDATE),
  okclawAuthUpdatePhone: invoke(RPC_CHANNELS.okclawAuth.UPDATE_PHONE),
  okclawAuthSendUserEmailValidate: invoke(RPC_CHANNELS.okclawAuth.SEND_USER_EMAIL_VALIDATE),
  okclawAuthSendNewEmailValidate: invoke(RPC_CHANNELS.okclawAuth.SEND_NEW_EMAIL_VALIDATE),
  okclawAuthUpdateEmail: invoke(RPC_CHANNELS.okclawAuth.UPDATE_EMAIL),
  okclawAuthSetEmail: invoke(RPC_CHANNELS.okclawAuth.SET_EMAIL),
  okclawAuthUpdatePassword: invoke(RPC_CHANNELS.okclawAuth.UPDATE_PASSWORD),
  okclawAuthSetPassword: invoke(RPC_CHANNELS.okclawAuth.SET_PASSWORD),
  okclawAuthUploadOssFile: invoke(RPC_CHANNELS.okclawAuth.UPLOAD_OSS_FILE),
  okclawAuthSubmitStudentCertification: invoke(RPC_CHANNELS.okclawAuth.SUBMIT_STUDENT_CERTIFICATION),
  okclawAuthCreateRechargeOrder: invoke(RPC_CHANNELS.okclawAuth.CREATE_RECHARGE_ORDER),
  okclawAuthLogout: invoke(RPC_CHANNELS.okclawAuth.LOGOUT),
  okclawAuthRejectNewSession: invoke(RPC_CHANNELS.okclawAuth.REJECT_NEW_SESSION),
  onOkclawAuthStateChanged: listener(RPC_CHANNELS.okclawAuth.STATE_CHANGED),
  onOkclawAuthSessionRevoked: listener(RPC_CHANNELS.okclawAuth.SESSION_REVOKED),

  // OKClaw Invoice
  okclawInvoiceGetOverview: invoke(RPC_CHANNELS.okclawInvoice.GET_OVERVIEW, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceListRecords: invoke(RPC_CHANNELS.okclawInvoice.LIST_RECORDS, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceCreateApplication: invoke(RPC_CHANNELS.okclawInvoice.CREATE_APPLICATION, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceRevokeApplication: invoke(RPC_CHANNELS.okclawInvoice.REVOKE_APPLICATION, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceDeleteApplication: invoke(RPC_CHANNELS.okclawInvoice.DELETE_APPLICATION, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceGetTitleUrl: invoke(RPC_CHANNELS.okclawInvoice.GET_TITLE_URL, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceCheckTitle: invoke(RPC_CHANNELS.okclawInvoice.CHECK_TITLE, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceListTitles: invoke(RPC_CHANNELS.okclawInvoice.LIST_TITLES, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceSaveTitle: invoke(RPC_CHANNELS.okclawInvoice.SAVE_TITLE, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceDeleteTitle: invoke(RPC_CHANNELS.okclawInvoice.DELETE_TITLE, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceListTaxCodes: invoke(RPC_CHANNELS.okclawInvoice.LIST_TAX_CODES, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceGetMaterialDetail: invoke(RPC_CHANNELS.okclawInvoice.GET_MATERIAL_DETAIL, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceApplyMaterial: invoke(RPC_CHANNELS.okclawInvoice.APPLY_MATERIAL, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceResendMaterial: invoke(RPC_CHANNELS.okclawInvoice.RESEND_MATERIAL, { timeoutMs: OKCLAW_INVOICE_CLIENT_TIMEOUT_MS }),
  okclawInvoiceDownloadFile: invoke(RPC_CHANNELS.okclawInvoice.DOWNLOAD_FILE, { timeoutMs: OKCLAW_INVOICE_DOWNLOAD_CLIENT_TIMEOUT_MS }),

  // OKClaw Subscription
  okclawSubGetBalance: invoke(RPC_CHANNELS.okclawSubscription.GET_BALANCE),
  okclawSubList: invoke(RPC_CHANNELS.okclawSubscription.LIST),
  okclawSubKeyList: invoke(RPC_CHANNELS.okclawSubscription.KEY_LIST),
  okclawSubAutoKey: invoke(RPC_CHANNELS.okclawSubscription.AUTO_KEY),
  okclawSubPlanList: invoke(RPC_CHANNELS.okclawSubscription.PLAN_LIST),
  okclawSubTrafficPackageList: invoke(RPC_CHANNELS.okclawSubscription.TRAFFIC_PACKAGE_LIST),
  okclawSubApiLogPage: invoke(RPC_CHANNELS.okclawSubscription.API_LOG_PAGE),
  okclawSubCreateBuyOrder: invoke(RPC_CHANNELS.okclawSubscription.CREATE_BUY_ORDER),
  okclawSubCreateRenewOrder: invoke(RPC_CHANNELS.okclawSubscription.CREATE_RENEW_ORDER),
  okclawSubCreateTrafficOrder: invoke(RPC_CHANNELS.okclawSubscription.CREATE_TRAFFIC_ORDER),
  okclawSubOrderDetail: invoke(RPC_CHANNELS.okclawSubscription.ORDER_DETAIL),
  okclawSubTransactionState: invoke(RPC_CHANNELS.okclawSubscription.TRANSACTION_STATE),
  okclawSubWxPrePay: invoke(RPC_CHANNELS.okclawSubscription.WX_PRE_PAY),
  okclawSubAlipayPrePay: invoke(RPC_CHANNELS.okclawSubscription.ALIPAY_PRE_PAY),
  okclawSubBalancePay: invoke(RPC_CHANNELS.okclawSubscription.BALANCE_PAY),
  okclawSubBalanceWxPrePay: invoke(RPC_CHANNELS.okclawSubscription.BALANCE_WX_PRE_PAY),
  okclawSubBalanceAlipayPrePay: invoke(RPC_CHANNELS.okclawSubscription.BALANCE_ALIPAY_PRE_PAY),
  okclawSubCouponList: invoke(RPC_CHANNELS.okclawSubscription.COUPON_LIST),
  okclawSubAvailableDiscountOptions: invoke(RPC_CHANNELS.okclawSubscription.AVAILABLE_DISCOUNT_OPTIONS),
  okclawSubCalculateDiscount: invoke(RPC_CHANNELS.okclawSubscription.CALCULATE_DISCOUNT),
  okclawSubAutoSetKeyAfterPurchase: invoke(RPC_CHANNELS.okclawSubscription.AUTO_SET_KEY_AFTER_PURCHASE),
  okclawSubUseSubscription: invoke(RPC_CHANNELS.okclawSubscription.USE_SUBSCRIPTION),
  okclawSubGetInviteInfo: invoke(RPC_CHANNELS.okclawSubscription.GET_INVITE_INFO),
  okclawSubKeyConfigGet: invoke(RPC_CHANNELS.okclawSubscription.KEY_CONFIG_GET),
  okclawSubKeyConfigSave: invoke(RPC_CHANNELS.okclawSubscription.KEY_CONFIG_SAVE),
  onOkclawSubKeyConfigUpdated: listener(RPC_CHANNELS.okclawSubscription.KEY_CONFIG_UPDATED),

  // Mail
  mailGetConfig: invoke(RPC_CHANNELS.mail.GET_CONFIG),
  mailSaveConfig: invoke(RPC_CHANNELS.mail.SAVE_CONFIG),
  mailTestConfig: invoke(RPC_CHANNELS.mail.TEST_CONFIG),

  // Feishu
  feishuGetConfig: invoke(RPC_CHANNELS.feishu.GET_CONFIG),
  feishuSaveConfig: invoke(RPC_CHANNELS.feishu.SAVE_CONFIG),
  feishuGetDecryptedSecret: invoke(RPC_CHANNELS.feishu.GET_DECRYPTED_SECRET),
  feishuTestConnection: invoke(RPC_CHANNELS.feishu.TEST_CONNECTION),
  feishuStartBridge: invoke(RPC_CHANNELS.feishu.START_BRIDGE),
  feishuStopBridge: invoke(RPC_CHANNELS.feishu.STOP_BRIDGE),
  feishuGetStatus: invoke(RPC_CHANNELS.feishu.GET_STATUS),
  feishuListBindings: invoke(RPC_CHANNELS.feishu.LIST_BINDINGS),
  feishuUpdateBinding: invoke(RPC_CHANNELS.feishu.UPDATE_BINDING),
  feishuRemoveBinding: invoke(RPC_CHANNELS.feishu.REMOVE_BINDING),
  feishuDeleteMessage: invoke(RPC_CHANNELS.feishu.DELETE_MESSAGE),
  feishuPinMessage: invoke(RPC_CHANNELS.feishu.PIN_MESSAGE),
  feishuEditMessage: invoke(RPC_CHANNELS.feishu.EDIT_MESSAGE),
  feishuForwardMessage: invoke(RPC_CHANNELS.feishu.FORWARD_MESSAGE),
  feishuUrgentMessage: invoke(RPC_CHANNELS.feishu.URGENT_MESSAGE),
  feishuGetUserInfo: invoke(RPC_CHANNELS.feishu.GET_USER_INFO),
  feishuListBindingsDisplay: invoke(RPC_CHANNELS.feishu.LIST_BINDINGS_DISPLAY),
  feishuRegisterApp: invoke(RPC_CHANNELS.feishu.REGISTER_APP_START, { timeoutMs: 5 * 60 * 1000 }),
  feishuCancelRegistration: invoke(RPC_CHANNELS.feishu.REGISTER_APP_CANCEL),
  onFeishuRegisterQrcode: listener(RPC_CHANNELS.feishu.REGISTER_APP_QRCODE),
  onFeishuRegisterStatus: listener(RPC_CHANNELS.feishu.REGISTER_APP_STATUS),

  // WeCom
  wecomGetConfig: invoke(RPC_CHANNELS.wecom.GET_CONFIG),
  wecomSaveConfig: invoke(RPC_CHANNELS.wecom.SAVE_CONFIG),
  wecomGetStatus: invoke(RPC_CHANNELS.wecom.GET_STATUS),
  wecomStartBridge: invoke(RPC_CHANNELS.wecom.START_BRIDGE),
  wecomStopBridge: invoke(RPC_CHANNELS.wecom.STOP_BRIDGE),
  wecomTestConnection: invoke(RPC_CHANNELS.wecom.TEST_CONNECTION),
  wecomRequestQr: invoke(RPC_CHANNELS.wecom.REQUEST_QR),
  wecomPollQr: invoke(RPC_CHANNELS.wecom.POLL_QR),
  wecomListBindings: invoke(RPC_CHANNELS.wecom.LIST_BINDINGS),
  wecomUpdateBinding: invoke(RPC_CHANNELS.wecom.UPDATE_BINDING),
  wecomRemoveBinding: invoke(RPC_CHANNELS.wecom.REMOVE_BINDING),

  // DingTalk
  dingtalkGetConfig: invoke(RPC_CHANNELS.dingtalk.GET_CONFIG),
  dingtalkSaveConfig: invoke(RPC_CHANNELS.dingtalk.SAVE_CONFIG),
  dingtalkGetDecryptedSecret: invoke(RPC_CHANNELS.dingtalk.GET_DECRYPTED_SECRET),
  dingtalkTestConnection: invoke(RPC_CHANNELS.dingtalk.TEST_CONNECTION),
  dingtalkStartBridge: invoke(RPC_CHANNELS.dingtalk.START_BRIDGE),
  dingtalkStopBridge: invoke(RPC_CHANNELS.dingtalk.STOP_BRIDGE),
  dingtalkGetStatus: invoke(RPC_CHANNELS.dingtalk.GET_STATUS),
  dingtalkListBindings: invoke(RPC_CHANNELS.dingtalk.LIST_BINDINGS),
  dingtalkUpdateBinding: invoke(RPC_CHANNELS.dingtalk.UPDATE_BINDING),
  dingtalkRemoveBinding: invoke(RPC_CHANNELS.dingtalk.REMOVE_BINDING),
  dingtalkGetUserInfo: invoke(RPC_CHANNELS.dingtalk.GET_USER_INFO),
  dingtalkSendImage: invoke(RPC_CHANNELS.dingtalk.SEND_IMAGE),
  dingtalkSendActionCard: invoke(RPC_CHANNELS.dingtalk.SEND_ACTION_CARD),
  dingtalkUploadFile: invoke(RPC_CHANNELS.dingtalk.UPLOAD_FILE),
  dingtalkSendFile: invoke(RPC_CHANNELS.dingtalk.SEND_FILE),

  // QQ Bot
  qqbotGetConfig: invoke(RPC_CHANNELS.qqbot.GET_CONFIG),
  qqbotSaveConfig: invoke(RPC_CHANNELS.qqbot.SAVE_CONFIG),
  qqbotGetDecryptedSecret: invoke(RPC_CHANNELS.qqbot.GET_DECRYPTED_SECRET),
  qqbotTestConnection: invoke(RPC_CHANNELS.qqbot.TEST_CONNECTION),
  qqbotStartBridge: invoke(RPC_CHANNELS.qqbot.START_BRIDGE),
  qqbotStopBridge: invoke(RPC_CHANNELS.qqbot.STOP_BRIDGE),
  qqbotGetStatus: invoke(RPC_CHANNELS.qqbot.GET_STATUS),
  qqbotListBindings: invoke(RPC_CHANNELS.qqbot.LIST_BINDINGS),
  qqbotUpdateBinding: invoke(RPC_CHANNELS.qqbot.UPDATE_BINDING),
  qqbotRemoveBinding: invoke(RPC_CHANNELS.qqbot.REMOVE_BINDING),

  // WeixinV2
  weixinV2GetConfig: invoke(RPC_CHANNELS.weixinV2.GET_CONFIG),
  weixinV2SaveConfig: invoke(RPC_CHANNELS.weixinV2.SAVE_CONFIG),
  weixinV2GetStatus: invoke(RPC_CHANNELS.weixinV2.GET_STATUS),
  weixinV2ListAccounts: invoke(RPC_CHANNELS.weixinV2.LIST_ACCOUNTS),
  weixinV2UpdateAccountRemark: invoke(RPC_CHANNELS.weixinV2.UPDATE_ACCOUNT_REMARK),
  weixinV2StartLogin: invoke(RPC_CHANNELS.weixinV2.START_LOGIN),
  weixinV2AwaitLogin: invoke(RPC_CHANNELS.weixinV2.AWAIT_LOGIN),
  weixinV2RemoveAccount: invoke(RPC_CHANNELS.weixinV2.REMOVE_ACCOUNT),
  weixinV2ListBindings: invoke(RPC_CHANNELS.weixinV2.LIST_BINDINGS),
  weixinV2UpdateBinding: invoke(RPC_CHANNELS.weixinV2.UPDATE_BINDING),
  weixinV2RemoveBinding: invoke(RPC_CHANNELS.weixinV2.REMOVE_BINDING),
} satisfies ChannelMap
